import { S3Client, PutObjectCommand, GetObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';

// Configuración del cliente S3 para Cloudflare R2
const r2Client = new S3Client({
    region: 'auto',
    endpoint: import.meta.env.VITE_R2_ENDPOINT,
    credentials: {
        accessKeyId: import.meta.env.VITE_R2_ACCESS_KEY_ID,
        secretAccessKey: import.meta.env.VITE_R2_SECRET_ACCESS_KEY,
    },
});

const BUCKET_NAME = import.meta.env.VITE_R2_BUCKET_NAME;
const PUBLIC_URL = import.meta.env.VITE_R2_PUBLIC_URL;

export interface UploadResult {
    success: boolean;
    url?: string;
    key?: string;
    error?: string;
}

/**
 * Sube un archivo a Cloudflare R2
 * @param file - Archivo a subir (File o Blob)
 * @param folder - Carpeta en R2 (ej: 'photos', 'cvs', 'pdfs')
 * @param userId - ID del usuario (para organización)
 * @returns Resultado con URL pública del archivo
 */
export async function uploadToR2(
    file: File | Blob,
    folder: string,
    userId: string
): Promise<UploadResult> {
    try {
        // Generar nombre único para el archivo
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 15);
        const extension = file instanceof File ? file.name.split('.').pop() : 'jpg';
        const fileName = `${timestamp}-${randomString}.${extension}`;

        // Construir la ruta completa: folder/userId/fileName
        const key = `${folder}/${userId}/${fileName}`;

        // Convertir archivo a buffer
        const arrayBuffer = await file.arrayBuffer();
        const buffer = Buffer.from(arrayBuffer);

        // Determinar Content-Type
        const contentType = file instanceof File ? file.type : 'application/octet-stream';

        // Subir a R2
        const command = new PutObjectCommand({
            Bucket: BUCKET_NAME,
            Key: key,
            Body: buffer,
            ContentType: contentType,
        });

        await r2Client.send(command);

        // Construir URL pública
        const publicUrl = `${PUBLIC_URL}/${key}`;

        console.log(`✅ Archivo subido exitosamente a R2: ${publicUrl}`);

        return {
            success: true,
            url: publicUrl,
            key: key,
        };
    } catch (error) {
        console.error('❌ Error al subir archivo a R2:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Elimina un archivo de R2
 * @param key - Clave del archivo en R2 (ej: 'photos/user123/file.jpg')
 */
export async function deleteFromR2(key: string): Promise<boolean> {
    try {
        const command = new DeleteObjectCommand({
            Bucket: BUCKET_NAME,
            Key: key,
        });

        await r2Client.send(command);
        console.log(`✅ Archivo eliminado de R2: ${key}`);
        return true;
    } catch (error) {
        console.error('❌ Error al eliminar archivo de R2:', error);
        return false;
    }
}

/**
 * Obtiene la URL pública de un archivo en R2
 * @param key - Clave del archivo
 */
export function getPublicUrl(key: string): string {
    return `${PUBLIC_URL}/${key}`;
}

/**
 * Extrae la clave (key) de una URL pública de R2
 * @param url - URL pública del archivo
 */
export function extractKeyFromUrl(url: string): string | null {
    try {
        const urlObj = new URL(url);
        // Remover el '/' inicial del pathname
        return urlObj.pathname.substring(1);
    } catch {
        return null;
    }
}

// Tipos de carpetas organizadas
export const R2_FOLDERS = {
    PHOTOS: 'photos',
    CVS: 'cvs',
    PDFS: 'generated-pdfs',
    TEMP: 'temp',
} as const;
